import { URLS } from '@components/playbook/urls'
import { KycStatus } from '@getstep/proto/common'
import { notNullish } from '@getstep/sdk/dist/util/Assert'
import type { AuthorizedStore } from '@lib/store/authorized/AuthorizedStore'
import type { NextRouter } from 'next/router'

/**
 * Unified enrollment steps
 */
enum EnrollmentStep {
    EMAIL_VERIFICATION = 'EMAIL_VERIFICATION',
    CARD_DETAILS = 'CARD_DETAILS',
    MEMBERSHIP_CONFIRMATION = 'MEMBERSHIP_CONFIRMATION',
    KYC = 'KYC',
    BANK_LINKING = 'BANK_LINKING',
    BORROW = 'BORROW',
    COMPLETED = 'COMPLETED',
}

/**
 * Enrollment flow step order
 * Flow: Email → KYC → Bank → Card → Subscription → Borrow
 */
const ENROLLMENT_STEPS: EnrollmentStep[] = [
    EnrollmentStep.EMAIL_VERIFICATION,
    EnrollmentStep.KYC,
    EnrollmentStep.BANK_LINKING,
    EnrollmentStep.CARD_DETAILS,
    EnrollmentStep.MEMBERSHIP_CONFIRMATION,
    EnrollmentStep.BORROW,
]

/**
 * State of user completion for enrollment steps
 */
interface EnrollmentState {
    hasEmail: boolean
    hasCard: boolean
    hasSubscription: boolean
    hasCompletedKyc: boolean
    hasBankAccount: boolean
}

/**
 * Enrollment Flow Manager
 *
 * Centralized manager for handling enrollment flow routing.
 * Determines which step the user should go to next based on their current completion state.
 *
 * Created once per session and stored on AuthorizedStore.
 *
 * Usage:
 * ```
 * const { enrollmentFlowManager } = authorizedStore
 * await enrollmentFlowManager.navigateToNextStep(router)
 * ```
 */
export class EnrollmentFlowManager {
    private authorizedStore: AuthorizedStore

    constructor(authorizedStore: AuthorizedStore) {
        this.authorizedStore = authorizedStore
    }

    /**
     * Navigate to the next step in the enrollment flow
     */
    async navigateToNextStep(router: NextRouter): Promise<void> {
        const url = this.getNextStepUrl()
        await router.replace(url)
    }

    /**
     * Get the URL for the next step without navigating
     * Useful for debugging or conditional navigation
     */
    getNextStepUrl(): string {
        const state = this.getEnrollmentState()
        const nextStep = this.getNextIncompleteStep(state, ENROLLMENT_STEPS)
        return this.getUrlForStep(nextStep)
    }

    /**
     * Check if user is on simplified enrollment flow
     * @deprecated Always returns true - simplified flow is the only flow
     */
    isUsingSimplifiedFlow(): boolean {
        return true
    }

    /**
     * Navigate to the previous step in the enrollment flow
     * Based on current URL
     */
    async navigateToPreviousStep(
        router: NextRouter,
        currentUrl: string,
    ): Promise<void> {
        const previousUrl = this.getPreviousStepUrl(currentUrl)
        if (previousUrl) {
            await router.replace(previousUrl)
        }
    }

    /**
     * Get the previous step URL based on current step
     * Returns null if there's no previous step (e.g., at the beginning)
     */
    getPreviousStepUrl(currentUrl: string): string | null {
        // Determine current step from URL
        if (currentUrl.includes(URLS.MEMBERSHIP_CONFIRMATION)) {
            // ... -> Card -> Subscription
            return URLS.CARD_DETAILS
        }

        if (currentUrl.includes(URLS.CARD_DETAILS)) {
            // ... -> Bank -> Card
            return URLS.STEP_BLACK_INTRO
        }

        if (currentUrl.includes(URLS.KYC_INFO)) {
            // Email -> KYC
            // Don't allow back from KYC
            return null
        }

        if (currentUrl.includes(URLS.STEP_BLACK_INTRO)) {
            return URLS.KYC_INFO
        }

        // No previous step for these screens
        return null
    }

    /**
     * Get the current enrollment state based on authorized store
     */
    private getEnrollmentState(): EnrollmentState {
        const hasEmail = notNullish(
            this.authorizedStore.userStore.verifiedEmailAlias,
        )

        // Check for card payment method
        const hasCard =
            this.authorizedStore.externalAccountsStore.accounts.some(
                (account: any) =>
                    account.card !== undefined && account.card !== null,
            )

        const hasSubscription = notNullish(
            this.authorizedStore.tierStore.tierSubscription,
        )

        const hasCompletedKyc =
            this.authorizedStore.userStore.user.kycStatus !==
                KycStatus.SKIPPED &&
            this.authorizedStore.userStore.user.kycStatus !== KycStatus.UNKNOWN

        // Check for bank account
        const hasBankAccount =
            this.authorizedStore.externalAccountsStore.accounts.some(
                (account: any) =>
                    account.bank !== undefined && account.bank !== null,
            )

        return {
            hasEmail,
            hasCard,
            hasSubscription,
            hasCompletedKyc,
            hasBankAccount,
        }
    }

    /**
     * Get the next incomplete step from the ordered steps array
     * Iterates through the steps in order and returns the first incomplete one
     */
    private getNextIncompleteStep(
        state: EnrollmentState,
        steps: EnrollmentStep[],
    ): EnrollmentStep {
        // Iterate through steps in order and find first incomplete
        for (const step of steps) {
            if (!this.isStepComplete(step, state)) {
                return step
            }
        }

        // All steps complete
        return EnrollmentStep.BORROW
    }

    /**
     * Check if a specific step is complete
     */
    private isStepComplete(
        step: EnrollmentStep,
        state: EnrollmentState,
    ): boolean {
        switch (step) {
            case EnrollmentStep.EMAIL_VERIFICATION:
                return state.hasEmail
            case EnrollmentStep.CARD_DETAILS:
                return state.hasCard
            case EnrollmentStep.MEMBERSHIP_CONFIRMATION:
                return state.hasSubscription
            case EnrollmentStep.KYC:
                return state.hasCompletedKyc
            case EnrollmentStep.BANK_LINKING:
                return state.hasBankAccount
            case EnrollmentStep.BORROW:
            case EnrollmentStep.COMPLETED:
                return true
            default:
                return false
        }
    }

    /**
     * Map enrollment step to URL
     */
    private getUrlForStep(step: EnrollmentStep): string {
        switch (step) {
            case EnrollmentStep.EMAIL_VERIFICATION:
                return URLS.EMAIL_VERIFICATION

            case EnrollmentStep.CARD_DETAILS:
                return URLS.CARD_DETAILS

            case EnrollmentStep.MEMBERSHIP_CONFIRMATION:
                return URLS.MEMBERSHIP_CONFIRMATION

            case EnrollmentStep.KYC:
                return URLS.KYC_INFO

            case EnrollmentStep.BANK_LINKING:
                return URLS.STEP_BLACK_INTRO

            case EnrollmentStep.BORROW:
                return URLS.BORROW

            case EnrollmentStep.COMPLETED:
                return URLS.BORROW

            default:
                return URLS.EXCLUSIVE_OFFER
        }
    }
}
